/**
 * Loading, empty, not-ready and failure states.
 *
 * The handoff notes these were not designed and are needed before build. They
 * are built here in the same language as the rest of the system: hairline
 * borders, mono labels, plain English, no dashed borders, no illustration.
 *
 * The rule they all follow: say what is true, and if a number cannot exist yet,
 * name the database object that would make it exist. Never show a plausible
 * figure in place of an absent one.
 */
import type { ReactNode } from 'react';
import { border, c, lockedGradient, mono, radius } from '../theme/tokens';
import { Mono } from './primitives';

/** Skeleton block used while a request is in flight. No spinners. */
export function Skeleton({ height = 16, width = '100%', radius: r = 4 }: { height?: number; width?: number | string; radius?: number }) {
  return (
    <div
      style={{
        height,
        width,
        borderRadius: r,
        background: c.lineSoft,
        animation: 'vpPulse 2.4s cubic-bezier(0.22,1,0.36,1) infinite',
      }}
    />
  );
}

export function LoadingPanel({ label = 'LOADING' }: { label?: string }) {
  return (
    <div
      style={{
        background: c.surface2,
        border: border.default,
        borderRadius: radius.card,
        padding: 28,
        display: 'flex',
        flexDirection: 'column',
        gap: 16,
      }}
    >
      <Mono size={10} tracking={0.12}>
        {label}
      </Mono>
      <Skeleton height={34} width="42%" />
      <Skeleton height={12} width="70%" />
      <Skeleton height={12} width="56%" />
    </div>
  );
}

/**
 * A surface that cannot be computed yet. Names the missing object rather than
 * apologising, so the reader knows it is a build step and not a fault.
 */
export function NotReady({
  title,
  body,
  objects,
  compact = false,
}: {
  title: string;
  body: string;
  objects: string[];
  compact?: boolean;
}) {
  return (
    <div
      style={{
        border: border.emphasis,
        background: lockedGradient,
        borderRadius: radius.card,
        padding: compact ? '18px 20px' : '24px 26px',
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
      }}
    >
      <Mono size={9.5} tracking={0.14} color={c.orange700}>
        AWAITING CALL ANALYSIS
      </Mono>
      <div style={{ fontSize: compact ? 15 : 16, lineHeight: 1.5, color: c.ink, textWrap: 'pretty' }}>{title}</div>
      <div style={{ fontSize: 13.5, lineHeight: 1.6, color: c.ink3, maxWidth: '64ch', textWrap: 'pretty' }}>{body}</div>
      {objects.length > 0 ? (
        <div
          style={{
            fontFamily: mono,
            fontSize: 10.5,
            lineHeight: 1.7,
            color: c.orange700,
            background: c.orange100,
            borderRadius: 8,
            padding: '8px 12px',
            marginTop: 2,
          }}
        >
          {objects.join(' · ')}
        </div>
      ) : null}
    </div>
  );
}

/**
 * Assistant is live, but nothing has come in for the selected window.
 *
 * The eyebrow names the window rather than saying "this range", and the actions
 * include widening it. "No calls in this range yet" with no statement of what
 * the range is and no control to change it left the reader unable to tell an
 * empty day from a broken dashboard.
 */
export function EmptyRange({
  rangeLabel,
  title,
  body,
  actions,
}: {
  rangeLabel: string;
  title: string;
  body: string;
  actions?: ReactNode;
}) {
  return (
    <div
      style={{
        background: c.surface2,
        border: border.default,
        borderRadius: radius.card,
        padding: '40px 32px',
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
        alignItems: 'flex-start',
      }}
    >
      <Mono size={10} tracking={0.12}>
        {rangeLabel.toUpperCase()}
      </Mono>
      <div style={{ fontSize: 21, lineHeight: 1.35, color: c.ink, maxWidth: '44ch', textWrap: 'pretty' }}>{title}</div>
      <div style={{ fontSize: 14.5, lineHeight: 1.6, color: c.ink3, maxWidth: '58ch', textWrap: 'pretty' }}>{body}</div>
      {actions ? (
        <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap', alignItems: 'center', marginTop: 2 }}>{actions}</div>
      ) : null}
    </div>
  );
}

/** First run — the assistant has no number attached to this branch yet. */
export function FirstRun({ branchName }: { branchName: string }) {
  return (
    <div
      style={{
        border: border.emphasis,
        background: lockedGradient,
        borderRadius: radius.card,
        padding: '36px 32px',
        display: 'flex',
        flexDirection: 'column',
        gap: 14,
        alignItems: 'flex-start',
      }}
    >
      <Mono size={9.5} tracking={0.14} color={c.indigo}>
        NOT CONNECTED YET
      </Mono>
      <div style={{ fontSize: 24, lineHeight: 1.3, color: c.ink, maxWidth: '44ch', textWrap: 'pretty' }}>
        {branchName} has no phone number attached to the assistant.
      </div>
      <div style={{ fontSize: 15, lineHeight: 1.6, color: c.ink2, maxWidth: '58ch', textWrap: 'pretty' }}>
        Once a number is pointed at the assistant, every call it answers appears here within a few seconds of
        the caller hanging up. Nothing on this screen is filled in before then.
      </div>
    </div>
  );
}

/** A request failed. States the status and offers a retry, nothing more. */
export function Failure({
  message,
  onRetry,
  label = 'COULD NOT LOAD',
}: {
  message: string;
  onRetry?: () => void;
  label?: string;
}) {
  return (
    <div
      style={{
        background: c.surface2,
        border: '1px solid rgba(215,38,61,0.28)',
        borderRadius: radius.card,
        padding: '26px 28px',
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
        alignItems: 'flex-start',
      }}
    >
      <Mono size={9.5} tracking={0.14} color={c.danger}>
        {label}
      </Mono>
      <div style={{ fontSize: 16, lineHeight: 1.55, color: c.ink, maxWidth: '58ch', textWrap: 'pretty' }}>{message}</div>
      {onRetry ? (
        <button
          className="vp-reset vp-outline"
          onClick={onRetry}
          style={{ padding: '9px 16px', borderRadius: radius.button, border: border.default, fontSize: 13.5, color: c.ink2 }}
        >
          Try again
        </button>
      ) : null}
    </div>
  );
}

/** Recording unavailable — the player's designed failure state. */
export function PlayerUnavailable({ reason }: { reason: string }) {
  return (
    <div
      style={{
        background: c.surface2,
        border: border.default,
        borderRadius: radius.card,
        padding: '22px 26px',
        display: 'flex',
        alignItems: 'center',
        gap: 18,
      }}
    >
      <span
        style={{
          width: 46,
          height: 46,
          flex: '0 0 46px',
          borderRadius: radius.pill,
          background: c.lineSoft,
          color: c.ink3,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 15,
        }}
      >
        ▶
      </span>
      <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 5 }}>
        <Mono size={10} tracking={0.12}>
          RECORDING UNAVAILABLE
        </Mono>
        <span style={{ fontSize: 14.5, lineHeight: 1.5, color: c.ink2 }}>{reason}</span>
      </div>
    </div>
  );
}

/** Inline em-dash for a metric that has no computable value. */
export function NoValue({ size = 34 }: { size?: number }) {
  return (
    <span style={{ fontSize: size, lineHeight: 1, letterSpacing: '-0.02em', color: 'rgba(11,18,32,0.22)' }}>——</span>
  );
}
